import { create } from "zustand";
import api, { apiFormData } from "../utils/api";
import { getError } from "../utils/getError";
import { useCompaniesStore } from "./admin/companiesStore";

export type TruckYardStatus = "active" | "inactive";
/**
 * Los tipos de personal de Patios. Cada valor es literalmente el `:type` de
 * `/truck-yard/:type` y tiene que coincidir con `ROLE_NAMES` del backend
 * (truck-yard/truckYardController.js).
 *
 * `yardAdmins` es el admin de patios (#558/#561): el rol existía en Firestore
 * desde el 29/7/2026 y no en ninguna de las dos capas, así que era asignable
 * desde Facturación → Usuarios y después nadie podía darle patios.
 */
export type TruckYardUserType =
  | "guards"
  | "supervisors"
  | "inspectors"
  | "admins"
  | "yardAdmins";

export interface TruckYardVehicle {
  id: string;
  name?: string;
  plate?: string;
  brand?: string;
  model?: string;
  year?: number;
  // Unit type (truck/pickup/…) — kept separate from water types so the admin
  // Units panel keeps working. The `string[]` case exists only for legacy
  // adapters that hand-build this object.
  type?: string | string[];
  // Per-vehicle subset of the group-inherited water types. When undefined the
  // vehicle shows the full inherited union; when set, it's a chosen subset
  // (UI guarantees it's non-empty + ⊆ inherited). Stored in its own field so
  // it no longer collides with the unit `type`.
  truck_yard_types?: string[];
  icon?: string;
  yard_ids?: string[];
  accessControl_id?: string;
  // Free-form PPD identifier and the validity window (ISO yyyy-MM-dd strings)
  // during which the patio assignment applies.
  ppd?: string;
  // NIS (service number) — truck-yard-only per-unit identifier.
  nis?: string;
  // Container capacity in m³ (free-form so the inspector can enter decimals).
  capacidad?: string;
  // Supplier unit: crosses the gate but discharges nothing, so its visits open
  // an inspection that is born "Cancelada". See deriveInspectionStatus.
  is_provider?: boolean;
  from?: string;
  to?: string;
  status?: { id: string; name: string } | string;
  status_id?: string;
  // GPS devices attached to this vehicle (resolved server-side in
  // /vehicleList via devices.vehicle_id). Used to feed the linked geofence's
  // `devices` when the vehicle is assigned to a patio.
  devices?: { id: string }[];
  createdAt?: { _seconds: number; _nanoseconds: number } | string;
  updatedAt?: { _seconds: number; _nanoseconds: number } | string;
}

export interface TruckYardVehicleAssignmentPayload {
  yard_ids: string[];
  accessControl_id?: string;
  truck_yard_types?: string[];
  ppd?: string;
  nis?: string;
  capacidad?: string;
  is_provider?: boolean;
  from?: string;
  to?: string;
  // Device IDs known by the client (from /vehicleList). The backend prefers
  // these and falls back to querying devices by vehicle_id.
  device_ids?: string[];
}

export interface TruckYard {
  id: string;
  name: string;
  location: string;
  latitude?: number;
  longitude?: number;
  radius?: number;
  guardHousePhone?: string;
  timeZone?: string;
  privacyPolicies?: string;
  status?: string;
  type?: string[];
  companyId?: string;
  logo?: string;
  // Id of the circular geofence auto-created for this yard. The yard is the
  // source of truth; the geofence mirrors its geometry (see backend
  // truckYardController). Read-only on the client.
  geofence_id?: string;
  // Mirror of the linked geofence's notifiedUsers, so the yard form can
  // prefill/edit them without fetching the geofence.
  notifiedUsers?: string[];
  // Mirror of the linked geofence's devices (device IDs). Managed manually
  // from the yard form and kept in sync from vehicle assignments.
  devices?: string[];
  // Mirror of the linked geofence's crossing events (entry/exit).
  geofenceActions?: string[];
  // Mirror of the linked geofence's sendEmails flag: if true, crossing events
  // also send email (not just push) to the notified users.
  sendEmails?: boolean;
  // `card_number` de los dos tags compartidos del patio: el de proveedores y
  // el de pipas sin tag. Ausente equivale a los dos en null (ver
  // normalizeSpecialTags en pages/patios-maniobras/specialTags.ts).
  special_tags?: { provider: string | null; untagged: string | null };
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
}

export interface TruckYardUser {
  id: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  status_id?: TruckYardStatus;
  status?: TruckYardStatus;
  yard_id?: string;
  yard_ids?: string[];
  yard?: TruckYard | null;
  yards?: TruckYard[];
  schedule?: any;
  /** Toggle por usuario; `undefined` cuenta como activa (ver personalSection). */
  geofence_validation_enabled?: boolean;
  identification_url?: string;
  createdAt?: any;
  updatedAt?: any;
}

export interface TruckYardUserPayload {
  firstName?: string;
  first_name?: string;
  lastName?: string;
  last_name?: string;
  email: string;
  phone?: string;
  password?: string;
  status?: TruckYardStatus;
  yard_id?: string;
  yard_ids?: string[];
  schedule?: any;
  geofence_validation_enabled?: boolean;
  identification_url?: string;
}

interface FetchParams {
  limit?: number;
  page?: number;
  search?: string;
  status?: string;
  yard_id?: string;
}

interface TruckYardStore {
  yards: TruckYard[];
  guards: TruckYardUser[];
  supervisors: TruckYardUser[];
  inspectors: TruckYardUser[];
  admins: TruckYardUser[];
  yardAdmins: TruckYardUser[];
  vehicles: TruckYardVehicle[];
  totalYards: number;
  totalGuards: number;
  totalSupervisors: number;
  totalInspectors: number;
  totalAdmins: number;
  totalVehicles: number;
  loading: boolean;
  usersLoading: boolean;
  vehiclesLoading: boolean;
  error: string | null;
  fetchYards: (params?: FetchParams) => Promise<void>;
  createYard: (formData: FormData) => Promise<TruckYard | null>;
  updateYard: (id: string, formData: FormData) => Promise<TruckYard | null>;
  deleteYard: (id: string) => Promise<boolean>;
  fetchYardUsers: (
    type: TruckYardUserType,
    params?: FetchParams,
  ) => Promise<void>;
  createYardUser: (
    type: TruckYardUserType,
    payload: TruckYardUserPayload,
  ) => Promise<TruckYardUser | null>;
  updateYardUser: (
    type: TruckYardUserType,
    id: string,
    payload: Partial<TruckYardUserPayload>,
  ) => Promise<TruckYardUser | null>;
  createYardUserWithFile: (
    type: TruckYardUserType,
    formData: FormData,
  ) => Promise<TruckYardUser | null>;
  updateYardUserWithFile: (
    type: TruckYardUserType,
    id: string,
    formData: FormData,
  ) => Promise<TruckYardUser | null>;
  deleteYardUser: (type: TruckYardUserType, id: string) => Promise<boolean>;
  resetUsers: (type: TruckYardUserType) => void;
  fetchVehicles: (params?: FetchParams) => Promise<void>;
  assignVehicleToYards: (
    vehicleId: string,
    payload: TruckYardVehicleAssignmentPayload,
  ) => Promise<TruckYardVehicle | null>;
}

const getUsersKey = (type: TruckYardUserType) => type;

export const useTruckYardStore = create<TruckYardStore>((set, get) => ({
  yards: [],
  guards: [],
  supervisors: [],
  inspectors: [],
  admins: [],
  yardAdmins: [],
  vehicles: [],
  totalYards: 0,
  totalGuards: 0,
  totalSupervisors: 0,
  totalInspectors: 0,
  totalAdmins: 0,
  totalVehicles: 0,
  loading: false,
  usersLoading: false,
  vehiclesLoading: false,
  error: null,

  fetchYards: async (params = {}) => {
    set({ loading: true, error: null });
    try {
      const { data } = await api.get("/truck-yard/yards", { params });
      set({
        yards: data.data || [],
        totalYards: data.meta?.totalItems || 0,
        loading: false,
      });
    } catch (err: any) {
      set({ error: getError(err), loading: false });
    }
  },

  createYard: async (formData) => {
    set({ loading: true, error: null });
    try {
      // Get companyId from companies store
      const companyId = useCompaniesStore.getState().companyId;

      // Add companyId to FormData
      if (companyId) {
        formData.append("companyId", companyId);
      }

      const { data } = await apiFormData.post("/truck-yard/yard", formData);
      const yard = data.data;
      set({ yards: [yard, ...get().yards], loading: false });
      return yard;
    } catch (err: any) {
      set({ error: getError(err), loading: false });
      return null;
    }
  },

  updateYard: async (id, formData) => {
    set({ loading: true, error: null });
    try {
      // Get companyId from companies store
      const companyId = useCompaniesStore.getState().companyId;

      // Add companyId to FormData
      if (companyId) {
        formData.append("companyId", companyId);
      }

      const { data } = await apiFormData.put("/truck-yard/yard", formData, {
        params: { id },
      });
      const yard = data.data;
      set({
        yards: get().yards.map((item) => (item.id === id ? yard : item)),
        loading: false,
      });
      return yard;
    } catch (err: any) {
      set({ error: getError(err), loading: false });
      return null;
    }
  },

  deleteYard: async (id) => {
    set({ loading: true, error: null });
    try {
      await api.delete("/truck-yard/yard", { params: { id } });
      set({
        yards: get().yards.filter((item) => item.id !== id),
        loading: false,
      });
      return true;
    } catch (err: any) {
      set({ error: getError(err), loading: false });
      return false;
    }
  },

  resetUsers: (type) => {
    const key = getUsersKey(type);
    const totalKey =
      `total${type.charAt(0).toUpperCase() + type.slice(1)}` as keyof TruckYardStore;
    set({
      [key]: [],
      [totalKey]: 0,
    } as Partial<TruckYardStore>);
  },

  fetchYardUsers: async (type, params = {}) => {
    set({ usersLoading: true, error: null });
    try {
      const { data } = await api.get(`/truck-yard/${type}`, { params });
      const totalKey =
        `total${type.charAt(0).toUpperCase() + type.slice(1)}` as keyof TruckYardStore;
      set({
        [getUsersKey(type)]: data.data || [],
        [totalKey]: data.meta?.totalItems || 0,
        usersLoading: false,
      } as Partial<TruckYardStore>);
    } catch (err: any) {
      set({ error: getError(err), usersLoading: false });
    }
  },

  createYardUser: async (type, payload) => {
    set({ usersLoading: true, error: null });
    try {
      const { data } = await api.post(`/truck-yard/${type}`, payload);
      const user = data.data;
      const key = getUsersKey(type);
      set({
        [key]: [user, ...get()[key]],
        usersLoading: false,
      } as Partial<TruckYardStore>);
      return user;
    } catch (err: any) {
      set({ error: getError(err), usersLoading: false });
      return null;
    }
  },

  updateYardUser: async (type, id, payload) => {
    set({ usersLoading: true, error: null });
    try {
      const { data } = await api.put(`/truck-yard/${type}`, payload, {
        params: { id },
      });
      const user = data.data;
      const key = getUsersKey(type);
      set({
        [key]: get()[key].map((item) =>
          item.id === id ? { ...user, id } : item,
        ),
        usersLoading: false,
      } as Partial<TruckYardStore>);
      return user;
    } catch (err: any) {
      set({ error: getError(err), usersLoading: false });
      return null;
    }
  },

  createYardUserWithFile: async (type, formData) => {
    set({ usersLoading: true, error: null });
    try {
      const { data } = await apiFormData.post(`/truck-yard/${type}`, formData);
      const user = data.data;
      const key = getUsersKey(type);
      set({
        [key]: [user, ...get()[key]],
        usersLoading: false,
      } as Partial<TruckYardStore>);
      return user;
    } catch (err: any) {
      set({ error: getError(err), usersLoading: false });
      return null;
    }
  },

  updateYardUserWithFile: async (type, id, formData) => {
    set({ usersLoading: true, error: null });
    try {
      const { data } = await apiFormData.put(`/truck-yard/${type}`, formData, {
        params: { id },
      });
      const user = data.data;
      const key = getUsersKey(type);
      set({
        [key]: get()[key].map((item) =>
          item.id === id ? { ...user, id } : item,
        ),
        usersLoading: false,
      } as Partial<TruckYardStore>);
      return user;
    } catch (err: any) {
      set({ error: getError(err), usersLoading: false });
      return null;
    }
  },

  deleteYardUser: async (type, id) => {
    set({ usersLoading: true, error: null });
    try {
      await api.delete(`/truck-yard/${type}`, { params: { id } });
      const key = getUsersKey(type);
      set({
        [key]: get()[key].filter((item) => item.id !== id),
        usersLoading: false,
      } as Partial<TruckYardStore>);
      return true;
    } catch (err: any) {
      set({ error: getError(err), usersLoading: false });
      return false;
    }
  },

  fetchVehicles: async (params = {}) => {
    set({ vehiclesLoading: true, error: null });
    try {
      const { data } = await api.get("/vehicleList", {
        params: {
          limit: params.limit ?? 100,
          page: params.page ?? 1,
          ...params,
        },
      });
      set({
        vehicles: data.data || [],
        totalVehicles:
          data.meta?.totalItems ??
          data.total ??
          (Array.isArray(data.data) ? data.data.length : 0),
        vehiclesLoading: false,
      });
    } catch (err: any) {
      set({ error: getError(err), vehiclesLoading: false });
    }
  },

  assignVehicleToYards: async (vehicleId, payload) => {
    set({ vehiclesLoading: true, error: null });
    try {
      const { data } = await api.put("/truck-yard/vehicle", payload, {
        params: { id: vehicleId },
      });
      const vehicle: TruckYardVehicle = data?.data ?? data;
      set({
        vehicles: get().vehicles.map((v) =>
          v.id === vehicleId ? { ...v, ...vehicle } : v,
        ),
        vehiclesLoading: false,
      });
      return vehicle;
    } catch (err: any) {
      set({ error: getError(err), vehiclesLoading: false });
      // Se relanza para que la pantalla distinga 403 (operador que cambia el
      // ID de acceso), 409 (tag de otro vehículo) y 404 (patio ajeno) del
      // error genérico. Su único llamador (vehiclesSection) ya tiene try/catch.
      throw err;
    }
  },
}));
